/* ==========================================================================
   Kizmit Designs — theme interactions
   Vanilla JS, no dependencies. Every scroll-driven effect reads from one
   requestAnimationFrame loop so the page stays smooth.
   ========================================================================== */
(() => {
  'use strict';

  const root = document.documentElement;
  const reduceMotion =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches || root.classList.contains('no-motion');
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const mod = (n, m) => ((n % m) + m) % m;
  /** Elements in scope matching sel that have not been set up yet. */
  const fresh = (sel, scope = document) =>
    $$(sel, scope).filter((el) => {
      if (el.dataset.bound) return false;
      el.dataset.bound = '1';
      return true;
    });

  /* ---------- Frame loop ---------- */
  const scrollHandlers = new Set();
  const tickHandlers = new Set();
  let lastY = window.scrollY;
  let velocity = 0;
  let lastTime = performance.now();

  function frame(now) {
    const dt = Math.min(64, now - lastTime) / 16.67;
    lastTime = now;
    const y = window.scrollY;
    velocity = lerp(velocity, y - lastY, 0.2);
    const scrolled = y !== lastY;
    lastY = y;
    if (scrolled || frame.force) {
      scrollHandlers.forEach((fn) => fn(y, velocity));
      frame.force = false;
    }
    tickHandlers.forEach((fn) => fn(dt, velocity));
    requestAnimationFrame(frame);
  }
  frame.force = true;
  requestAnimationFrame(frame);
  window.addEventListener('resize', () => { frame.force = true; });

  /** Progress (0..1) of a tall element scrolling past its sticky child. */
  function stickyProgress(el) {
    const rect = el.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    if (total <= 0) return rect.top <= 0 ? 1 : 0;
    return clamp(-rect.top / total, 0, 1);
  }

  /** Runs callback only while element is near the viewport. */
  function whenVisible(el, onChange) {
    const io = new IntersectionObserver((entries) => entries.forEach((e) => onChange(e.isIntersecting)), {
      rootMargin: '200px 0px'
    });
    io.observe(el);
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal(scope) {
    const items = fresh('.reveal, .reveal-clip', scope);
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    // Clip-path reveals are observed through their parent: a fully clipped
    // element can report as not intersecting in some browsers.
    const targets = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          (targets.get(e.target) || []).forEach((el) => el.classList.add('is-in'));
          targets.delete(e.target);
          io.unobserve(e.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
    );
    items.forEach((el) => {
      const watch = el.classList.contains('reveal-clip') ? el.parentElement : el;
      if (!targets.has(watch)) targets.set(watch, []);
      targets.get(watch).push(el);
      io.observe(watch);
    });
  }

  /* ---------- Header ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const hero = $('[data-hero], [data-chero], [data-ahero], .mpage__hero');
    const overlay = header.hasAttribute('data-overlay');
    let lastScroll = 0;
    scrollHandlers.add((y) => {
      if (overlay) {
        const solidAt = hero ? hero.offsetTop + hero.offsetHeight - header.offsetHeight - 10 : 40;
        header.classList.toggle('is-solid', y > solidAt);
      }
      const goingDown = y > lastScroll && y > 400;
      header.classList.toggle('is-hidden', goingDown && !header.matches(':focus-within'));
      lastScroll = y;
    });

    const drawer = $('[data-drawer]');
    if (!drawer) return;
    const open = () => {
      drawer.hidden = false;
      document.body.style.overflow = 'hidden';
      $('[data-drawer-close]', drawer)?.focus();
    };
    const close = () => {
      drawer.hidden = true;
      document.body.style.overflow = '';
    };
    $$('[data-drawer-open]').forEach((b) => b.addEventListener('click', open));
    $$('[data-drawer-close]', drawer).forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !drawer.hidden) close(); });
  }

  /* ---------- Hero: silk curtain unveil + slides ---------- */
  function initHero(scope) {
    fresh('[data-hero]', scope).forEach(setupHero);
  }
  function setupHero(hero) {
    const hasIntro = hero.dataset.intro === 'true' && !reduceMotion;
    const track = $('.hero__track', hero);

    if (hasIntro) {
      let current = 0;
      let target = 0;
      scrollHandlers.add(() => {
        target = stickyProgress(track);
      });
      tickHandlers.add(() => {
        if (Math.abs(target - current) < 0.0005) return;
        current = lerp(current, target, 0.14);
        // Ease-in-out-sine: the fabric starts lifting at once, then sweeps away.
        const eased = -(Math.cos(Math.PI * current) - 1) / 2;
        hero.style.setProperty('--p', Math.min(1, eased * 1.08).toFixed(4));
        hero.classList.toggle('is-unveiled', eased >= 0.97);
      });
    } else {
      hero.style.setProperty('--p', 1);
      hero.classList.add('is-unveiled');
    }

    const slides = $$('[data-hero-slide]', hero);
    const captions = $$('[data-hero-caption]', hero);
    const count = Math.max(slides.length, captions.length);
    if (count < 2) return;
    const seconds = parseFloat(hero.dataset.seconds || '6');
    let i = 0;
    setInterval(() => {
      if (document.hidden) return;
      i = (i + 1) % count;
      slides.forEach((s, n) => s.classList.toggle('is-active', n === i % slides.length));
      captions.forEach((c, n) => c.classList.toggle('is-active', n === i % captions.length));
    }, seconds * 1000);
  }

  /* ---------- Curved 3D carousel ("ring") ---------- */
  function initRings(scope) {
    fresh('[data-ring]', scope).forEach((ring) => {
      const stage = $('[data-ring-stage]', ring);
      const cards = $$('[data-ring-card]', ring);
      const n = cards.length;
      if (!n) return;

      let offset = 0; // in card units
      let dragVel = 0;
      let dragging = false;
      let visible = false;
      let startX = 0;
      let startOffset = 0;
      let moved = 0;
      const autoSpeed = reduceMotion ? 0 : parseFloat(ring.dataset.ringSpeed || '3') * 0.0016;

      function layout() {
        const w = stage.clientWidth;
        const cardW = cards[0].offsetWidth;
        const spacing = cardW * 1.02;
        const radius = Math.max(w * 0.62, spacing * 4);
        const stepAngle = spacing / radius; // radians per card
        cards.forEach((card, idx) => {
          let o = mod(idx - offset + n / 2, n) - n / 2; // wrapped distance from centre
          const a = o * stepAngle;
          const x = Math.sin(a) * radius;
          // Viewer sits inside the cylinder: edges curve towards the camera.
          const z = (1 - Math.cos(a)) * radius * 0.9;
          const hidden = Math.abs(a) > 1.25;
          card.style.transform = `translate3d(${x.toFixed(1)}px, ${(Math.abs(o) * 6).toFixed(1)}px, ${z.toFixed(1)}px) rotateY(${(-a * 57.2958 * 0.95).toFixed(2)}deg)`;
          card.style.opacity = hidden ? '0' : '1';
          card.style.zIndex = String(100 - Math.round(Math.abs(o) * 2));
          card.style.visibility = hidden ? 'hidden' : 'visible';
        });
      }

      whenVisible(ring, (v) => { visible = v; });
      let lastScrollY = window.scrollY;
      scrollHandlers.add((y) => {
        if (!visible || reduceMotion) return;
        offset += (y - lastScrollY) * 0.004;
        lastScrollY = y;
      });
      tickHandlers.add((dt) => {
        if (!visible) return;
        if (!dragging) {
          offset += autoSpeed * dt + dragVel;
          dragVel *= 0.94;
        }
        layout();
      });

      stage.addEventListener('pointerdown', (e) => {
        dragging = true;
        moved = 0;
        startX = e.clientX;
        startOffset = offset;
        stage.classList.add('is-dragging');
        stage.setPointerCapture(e.pointerId);
      });
      stage.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const cardW = cards[0].offsetWidth;
        const dx = e.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));
        const next = startOffset - dx / (cardW * 1.1);
        dragVel = next - offset;
        offset = next;
      });
      const end = () => {
        dragging = false;
        stage.classList.remove('is-dragging');
      };
      stage.addEventListener('pointerup', end);
      stage.addEventListener('pointercancel', end);
      stage.addEventListener('click', (e) => { if (moved > 6) e.preventDefault(); }, true);
      layout();
    });
  }

  /* ---------- Horizontal rails ---------- */
  function initRails(scope) {
    fresh('[data-rail]', scope).forEach((rail) => {
      const sec = rail.closest('section') || document;
      const EPS = 2;
      // A rail may hold as few as five or six cards, so a single step can
      // consume the whole scroll range and every later click does nothing.
      // Wrap around at the ends so the arrows always move the rail.
      const go = (dir) => {
        const max = rail.scrollWidth - rail.clientWidth;
        if (max <= EPS) return;
        const at = rail.scrollLeft;
        const step = rail.clientWidth * 0.8;
        let left;
        if (dir > 0) left = at >= max - EPS ? 0 : Math.min(max, at + step);
        else left = at <= EPS ? max : Math.max(0, at - step);
        rail.scrollTo({ left, behavior: 'smooth' });
      };
      $('[data-rail-prev]', sec)?.addEventListener('click', () => go(-1));
      $('[data-rail-next]', sec)?.addEventListener('click', () => go(1));
    });
  }

  /* ---------- Simple cross-fade slideshows (bridal feature) ---------- */
  function initSlideshows(scope) {
    fresh('[data-slideshow]', scope).forEach((el) => {
      const slides = $$('[data-slide]', el);
      if (slides.length < 2 || reduceMotion) return;
      let i = 0;
      const ms = parseFloat(el.dataset.interval || '4') * 1000;
      setInterval(() => {
        if (document.hidden) return;
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }, ms);
    });
  }

  /* ---------- Drifting compact rows ---------- */
  function initDrift(scope) {
    fresh('[data-drift]', scope).forEach((viewport) => {
      const dir = viewport.dataset.drift;
      const track = $('[data-drift-track]', viewport);
      if (!track || dir === 'none' || reduceMotion) {
        viewport.style.overflowX = 'auto';
        return;
      }
      Array.from(track.children).forEach((c) => {
        const clone = c.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button, input').forEach((f) => f.setAttribute('tabindex', '-1'));
        track.appendChild(clone);
      });
      let x = 0;
      let paused = false;
      let visible = false;
      const speed = dir === 'right' ? 0.35 : -0.35;
      viewport.addEventListener('pointerenter', () => { paused = true; });
      viewport.addEventListener('pointerleave', () => { paused = false; });
      viewport.addEventListener('focusin', () => { paused = true; });
      viewport.addEventListener('focusout', () => { paused = false; });
      whenVisible(viewport, (v) => { visible = v; });
      tickHandlers.add((dt, vel) => {
        if (!visible || paused) return;
        const half = track.scrollWidth / 2;
        x += speed * dt * (1 + Math.min(6, Math.abs(vel) * 0.15));
        if (x <= -half) x += half;
        if (x > 0) x -= half;
        track.style.transform = `translate3d(${x.toFixed(2)}px,0,0)`;
      });
    });
  }

  /* ---------- Size chips → variant ---------- */
  function readVariants(scope) {
    const node = $('[data-variants]', scope);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }
  function initChips() {
    document.addEventListener('click', (e) => {
      const chip = e.target.closest('.chip[data-size]');
      if (!chip) return;
      const group = chip.parentElement;
      $$('.chip', group).forEach((c) => {
        c.classList.toggle('is-active', c === chip);
        c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
      });
      const scope = chip.closest('[data-compact-card], .drop__panel');
      if (!scope) return;
      const variants = readVariants(scope);
      if (!variants) return;
      const match = variants.find((v) => v.size === chip.dataset.size);
      if (!match) return;
      $$('[data-variant-input]', scope).forEach((input) => { input.value = match.id; });
      const btn = $('button[type="submit"]', scope);
      if (btn) btn.disabled = !match.available;
    });
  }

  /* ---------- Quantity steppers ---------- */
  function initQty() {
    document.addEventListener('click', (e) => {
      const minus = e.target.closest('[data-qty-minus]');
      const plus = e.target.closest('[data-qty-plus]');
      if (!minus && !plus) return;
      const input = $('input', (minus || plus).parentElement);
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) + (plus ? 1 : -1));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- Cart ---------- */
  const toast = $('[data-cart-toast]');
  let toastTimer;
  function showToast(title, image) {
    if (!toast) return;
    $('[data-toast-title]', toast).textContent = title || '';
    const img = $('[data-toast-img]', toast);
    if (image) { img.src = image; img.hidden = false; } else { img.hidden = true; }
    toast.classList.add('is-open');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-open'), 3200);
  }
  async function refreshCount() {
    try {
      const res = await fetch(window.theme.routes.cartJs, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch (e) { /* ignore */ }
  }
  async function addToCart(form, button) {
    const body = form instanceof FormData ? form : new FormData(form);
    button?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(window.theme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body
      });
      if (!res.ok) throw new Error('add failed');
      await refreshCount();
      showToast(button?.dataset.title, button?.dataset.image);
      return true;
    } catch (e) {
      showToast(window.theme.strings.error);
      return false;
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }
  function initCart() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-quick-add], [data-drop-form], [data-pdp-form]');
      if (!form) return;
      e.preventDefault();
      const button = e.submitter || $('button[type="submit"]', form);
      if (button?.hasAttribute('data-demo-add')) {
        markAdded(button);
        showToast(button.dataset.title);
        return;
      }
      const ok = await addToCart(form, button);
      if (ok) markAdded(button);
    });
    document.addEventListener('click', (e) => {
      const demo = e.target.closest('button[data-demo-add]:not([type="submit"])');
      if (!demo) return;
      markAdded(demo);
      const card = demo.closest('article, .story__metaitem');
      const title = card?.querySelector('h3, .c-card__title, .b-card__title')?.textContent.trim();
      showToast(title);
    });
  }
  function initQuickVariant() {
    document.addEventListener('click', async (e) => {
      const b = e.target.closest('[data-quick-variant]');
      if (!b) return;
      e.preventDefault();
      const body = new FormData();
      body.append('id', b.dataset.quickVariant);
      body.append('quantity', '1');
      const ok = await addToCart(body, b);
      if (ok) markAdded(b);
    });
  }
  function markAdded(button) {
    if (!button) return;
    button.classList.add('is-added');
    setTimeout(() => button.classList.remove('is-added'), 2600);
  }

  /* ---------- New drop spotlight ---------- */
  function initSpotlight(scope) {
    fresh('[data-spotlight]', scope).forEach((sec) => {
      const looks = $$('[data-look]', sec);
      const panels = $$('[data-panel]', sec);
      const thumbs = $$('.drop__thumb', sec);
      const n = looks.length;
      if (!n) return;
      let current = 0;
      function go(i) {
        const next = mod(i, n);
        if (next === current) return;
        const dir = next > current ? 1 : -1;
        looks.forEach((l, k) => {
          l.style.setProperty('--dir', dir);
          l.classList.toggle('is-active', k === next);
        });
        panels.forEach((p, k) => {
          const on = k === next;
          p.classList.toggle('is-active', on);
          p.hidden = !on;
        });
        thumbs.forEach((t, k) => {
          t.classList.toggle('is-active', k === next);
          t.setAttribute('aria-selected', k === next ? 'true' : 'false');
        });
        $$('.drop__swatch', sec).forEach((s) => s.classList.toggle('is-active', Number(s.dataset.lookBtn) === next));
        current = next;
      }
      sec.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-look-btn]');
        if (btn) go(Number(btn.dataset.lookBtn));
        if (e.target.closest('[data-look-prev]')) go(current - 1);
        if (e.target.closest('[data-look-next]')) go(current + 1);
      });
      // swipe on the stage
      const stage = $('.drop__stage', sec);
      let sx = null;
      stage?.addEventListener('pointerdown', (e) => { sx = e.clientX; });
      stage?.addEventListener('pointerup', (e) => {
        if (sx === null) return;
        const dx = e.clientX - sx;
        if (Math.abs(dx) > 50) go(current + (dx < 0 ? 1 : -1));
        sx = null;
      });
    });
  }

  /* ---------- Occasion edits: title echo follows scroll speed ---------- */
  function initEcho(scope) {
    const titles = fresh('[data-echo]', scope);
    if (!titles.length || reduceMotion) return;
    let echo = 0;
    tickHandlers.add((dt, vel) => {
      const target = clamp(vel * 0.6, -14, 14);
      echo = lerp(echo, target, 0.15);
      if (Math.abs(echo) < 0.05 && Math.abs(target) < 0.05) return;
      titles.forEach((t) => {
        t.style.setProperty('--echo', echo.toFixed(2));
        t.style.setProperty('--echo-o', Math.min(1, Math.abs(echo) / 5).toFixed(2));
      });
    });
  }

  /* ---------- Offers accordion ---------- */
  function initOffers(scope) {
    fresh('[data-accordion]', scope).forEach((row) => {
      const items = $$('[data-offer]', row);
      const open = (item) => items.forEach((i) => i.classList.toggle('is-open', i === item));
      items.forEach((item) => {
        item.addEventListener('mouseenter', () => open(item));
        item.addEventListener('focusin', () => open(item));
        item.querySelector('a').addEventListener('click', (e) => {
          if (!item.classList.contains('is-open')) {
            e.preventDefault();
            open(item);
          }
        });
      });
    });
  }

  /* ---------- Story gallery ---------- */
  function initStory(scope) {
    fresh('[data-story]', scope).forEach((story) => {
      const track = $('.story__track', story);
      const sticky = $('.story__sticky', story);
      const bgs = $$('[data-story-bg]', story);
      const titles = $$('[data-story-title]', story);
      const metas = $$('[data-story-meta]', story);
      const thumbs = $$('[data-story-thumb]', story);
      const strip = $('[data-story-strip]', story);
      const indexEl = $('[data-story-index]', story);
      const bar = $('[data-story-bar]', story);
      const n = bgs.length;
      if (!n) return;
      let current = -1;
      let dragShift = 0;

      function setActive(i) {
        if (i === current) return;
        current = i;
        [bgs, titles, metas, thumbs].forEach((list) => list.forEach((el, k) => el.classList.toggle('is-active', k === i)));
        if (indexEl) indexEl.textContent = String(i + 1).padStart(2, '0');
        centerStrip();
      }
      function centerStrip() {
        const t = thumbs[current];
        if (!t || !strip) return;
        const first = thumbs[0];
        const shift = t.offsetLeft - first.offsetLeft;
        strip.style.setProperty('--strip-x', `${-shift * 0.6 + dragShift}px`);
      }
      function scrollToChapter(i) {
        const target = clamp(i, 0, n - 1);
        if (reduceMotion) { setActive(target); return; }
        const total = track.offsetHeight - window.innerHeight;
        const top = story.getBoundingClientRect().top + window.scrollY;
        const y = top + (total * (target + 0.5)) / n;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }

      if (reduceMotion) {
        setActive(0);
      } else {
        scrollHandlers.add(() => {
          const p = stickyProgress(track);
          if (bar) bar.style.setProperty('--story-p', p.toFixed(3));
          setActive(clamp(Math.floor(p * n * 0.999), 0, n - 1));
        });
      }
      if (reduceMotion && bar) story.style.setProperty('--story-p', 1);

      thumbs.forEach((t, i) => t.addEventListener('click', (e) => {
        if (story.dataset.dragged === '1') { e.preventDefault(); return; }
        scrollToChapter(i);
        if (reduceMotion) setActive(i);
      }));

      sticky.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); scrollToChapter(current + 1); if (reduceMotion) setActive(Math.min(n - 1, current + 1)); }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); scrollToChapter(current - 1); if (reduceMotion) setActive(Math.max(0, current - 1)); }
      });
      // Arrow keys also work while the gallery fills the screen, without needing focus.
      document.addEventListener('keydown', (e) => {
        if (document.activeElement && /INPUT|TEXTAREA|SELECT/.test(document.activeElement.tagName)) return;
        if (sticky.contains(document.activeElement)) return;
        const r = sticky.getBoundingClientRect();
        const inView = r.top <= 1 && r.bottom >= window.innerHeight - 1;
        if (!inView) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); scrollToChapter(current + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); scrollToChapter(current - 1); }
      });

      // Drag the strip or the scene to move between chapters.
      let sx = null;
      let moved = 0;
      sticky.addEventListener('pointerdown', (e) => {
        if (e.target.closest('form, a')) return;
        sx = e.clientX;
        moved = 0;
        story.dataset.dragged = '0';
      });
      window.addEventListener('pointermove', (e) => {
        if (sx === null) return;
        const dx = e.clientX - sx;
        moved = Math.abs(dx);
        if (moved > 6) {
          story.classList.add('is-dragging');
          dragShift = dx * 0.5;
          centerStrip();
        }
      });
      window.addEventListener('pointerup', (e) => {
        if (sx === null) return;
        const dx = e.clientX - sx;
        sx = null;
        story.classList.remove('is-dragging');
        dragShift = 0;
        if (moved > 60) {
          story.dataset.dragged = '1';
          const next = current + (dx < 0 ? 1 : -1);
          scrollToChapter(next);
          if (reduceMotion) setActive(clamp(next, 0, n - 1));
          setTimeout(() => { story.dataset.dragged = '0'; }, 50);
        }
        centerStrip();
      });
      setActive(0);
      window.addEventListener('resize', centerStrip);
    });
  }

  /* ---------- Latest trends: 3D photo deck ---------- */
  function initDeck(scope) {
    fresh('[data-deck]', scope).forEach((deck) => {
      const cards = $$('[data-deck-card]', deck);
      const stack = $('[data-deck-stack]', deck);
      const indexEl = $('[data-deck-index]', deck);
      const n = cards.length;
      if (!n) return;
      let current = 0;
      function render() {
        cards.forEach((c, i) => {
          let pos = mod(i - current, n);
          if (pos === n - 1 && n > 2) pos = -1;
          c.dataset.pos = String(Math.min(pos, 3));
          c.setAttribute('tabindex', pos === 0 ? '0' : '-1');
        });
        if (indexEl) indexEl.textContent = String(current + 1);
      }
      const go = (d) => { current = mod(current + d, n); render(); };
      $('[data-deck-prev]', deck)?.addEventListener('click', () => go(-1));
      $('[data-deck-next]', deck)?.addEventListener('click', () => go(1));
      let sx = null;
      let moved = 0;
      stack.addEventListener('pointerdown', (e) => { sx = e.clientX; moved = 0; });
      stack.addEventListener('pointermove', (e) => { if (sx !== null) moved = Math.abs(e.clientX - sx); });
      stack.addEventListener('pointerup', (e) => {
        if (sx === null) return;
        const dx = e.clientX - sx;
        sx = null;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
      });
      stack.addEventListener('click', (e) => { if (moved > 6) e.preventDefault(); }, true);
      render();
      if (!reduceMotion) {
        let visible = false;
        whenVisible(deck, (v) => { visible = v; });
        setInterval(() => { if (visible && !document.hidden && !deck.matches(':hover')) go(1); }, 4200);
      }
    });
  }

  /* ---------- Parallax (forecast arches) ---------- */
  function initParallax(scope) {
    const items = fresh('[data-parallax]', scope);
    if (!items.length || reduceMotion) return;
    scrollHandlers.add(() => {
      const vh = window.innerHeight;
      items.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        const centre = r.top + r.height / 2 - vh / 2;
        const f = parseFloat(el.dataset.parallax) || 0;
        el.style.transform = `translate3d(0, ${(centre * f).toFixed(1)}px, 0)`;
      });
    });
  }

  /* ---------- Wishlist (saved in this browser) ---------- */
  function initWishlist() {
    const KEY = 'kizmit:wishlist';
    let list = [];
    try { list = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { list = []; }
    const save = () => { try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* storage blocked */ } };
    const paint = () => {
      $$('[data-wishlist]').forEach((b) => b.setAttribute('aria-pressed', list.includes(b.dataset.wishlist) ? 'true' : 'false'));
      $$('[data-wishlist-count]').forEach((c) => { c.textContent = list.length; c.hidden = list.length === 0; });
    };
    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-wishlist]');
      if (!b) return;
      e.preventDefault();
      const id = b.dataset.wishlist;
      list = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
      save();
      paint();
    });
    paint();
  }

  /* ---------- Share ---------- */
  function initShare() {
    document.addEventListener('click', async (e) => {
      const b = e.target.closest('[data-share]');
      if (!b) return;
      const url = b.dataset.share || location.href;
      if (navigator.share) {
        try { await navigator.share({ url }); } catch (err) { /* cancelled */ }
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        showToast('Link copied');
      }
    });
  }

  function initScope(scope) {
    initReveal(scope);
    initHero(scope);
    initRings(scope);
    initRails(scope);
    initSlideshows(scope);
    initDrift(scope);
    initSpotlight(scope);
    initEcho(scope);
    initOffers(scope);
    initStory(scope);
    initDeck(scope);
    initParallax(scope);
    frame.force = true;
  }

  // Shared helpers for product.js and pages.js.
  window.Kizmit = {
    $, $$, clamp, lerp, mod, fresh, reduceMotion,
    scrollHandlers, tickHandlers, stickyProgress, whenVisible,
    addToCart, showToast, markAdded, refreshCount, readVariants, initScope,
    frame
  };

  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initQuickVariant();
    initChips();
    initQty();
    initCart();
    initWishlist();
    initShare();
    initScope(document);
  });

  // Set up sections that merchants add or edit in the theme editor.
  document.addEventListener('shopify:section:load', (e) => initScope(e.target));
})();
